/* Epic draft presentation adapter. The app owns API data and variant updates. */
(() => {
  'use strict';
  const PS = window.ProductSpecs;
  if (!PS || PS.createDom.epicThemePresentation) return;
  const createDom = PS.createDom;
  PS.createDom = function (ctx) {
    const dom = createDom(ctx);
    const root = ctx.specsElement.closest('.epic-specifications-block');
    if (!root) return dom;
    const groups = PS.epicThemeGroups;
    const customGroups = new Map();
    root.querySelectorAll('template[data-epic-spec-group]').forEach(template => {
      const content = template.content;
      if (content.textContent.replace(/[\s\u00a0\u200b]/g, '') || content.querySelector('img,video')) {
        customGroups.set(template.dataset.epicSpecGroup, template.innerHTML.trim());
      }
    });
    function withGroupOverrides(sections) {
      if (!customGroups.size || !groups) return sections;
      const specifications = sections.find(section => section.key === 'specifications');
      const api = specifications?.renderKind === 'specifications_table'
        ? specifications : specifications?.apiFallback;
      const rows = api?.renderKind === 'specifications_table' && Array.isArray(api.content?.rows)
        ? api.content.rows.filter(row => !customGroups.has(groups.getCategory(row))) : [];
      customGroups.forEach((html, category) => {
        rows.push({ key: category, label: groups.titles[category], value: html });
      });
      const merged = {
        ...specifications, key: 'specifications', label: specifications?.label || 'Specs',
        source: 'metafield', renderKind: 'specifications_table', content: { rows }, hasContent: true,
      };
      const independentGroupKeys = new Set([
        'product:custom.tech_specs', 'product:custom.groupset', 'product:custom.wheelset',
      ]);
      const result = sections.filter(section => !independentGroupKeys.has(section.key));
      return specifications
        ? result.map(section => section.key === 'specifications' ? merged : section)
        : [...result, merged];
    }
    function customSpecNodes(html) {
      const source = document.createElement('div');
      ctx.safe.setHtml(source, html);
      // The app sanitizer strips CSS classes from rich-text wrappers.
      let field = source;
      while (field.children.length === 1 && field.firstElementChild.tagName === 'DIV') {
        field = field.firstElementChild;
      }
      const nodes = [...field.children];
      const result = [];
      const readableLabel = text => {
        const label = text.trim();
        return label === label.toUpperCase()
          ? label.charAt(0) + label.slice(1).toLowerCase() : label;
      };
      const hasContent = node => node.textContent.trim() || node.querySelector('img,video');
      // Shopify rich-text fields also store specifications as "Label: value" paragraphs.
      // Split the sanitized DOM so inline formatting and links in the value survive.
      const paragraphItem = node => {
        if (node.tagName !== 'P') return null;
        const text = node.textContent;
        const separator = text.indexOf(':');
        if (separator < 1) return null;
        const labelText = text.slice(0, separator).trim();
        const valueText = text.slice(separator + 1);
        if (!/^[\p{L}\p{N}][\p{L}\p{N}\s/&()._-]{0,79}$/u.test(labelText)
          || /^(https?|mailto|tel)$/i.test(labelText)
          || !valueText.trim() || valueText.trimStart().startsWith('//')) return null;
        let offset = separator + 1 + valueText.length - valueText.trimStart().length;
        const walker = document.createTreeWalker(node, 4); // SHOW_TEXT
        let textNode = walker.nextNode();
        while (textNode && offset >= textNode.textContent.length) {
          offset -= textNode.textContent.length;
          textNode = walker.nextNode();
        }
        if (!textNode) return null;
        const range = document.createRange();
        range.selectNodeContents(node);
        range.setStart(textNode, offset);
        const value = range.cloneContents();
        node.replaceChildren(value);
        const label = document.createElement('strong');
        ctx.safe.setText(label, readableLabel(labelText));
        const item = document.createElement('div');
        item.className = 'epic-specifications-custom-item';
        item.append(label, node);
        return item;
      };
      for (let index = 0; index < nodes.length; index += 1) {
        const node = nodes[index];
        if (!hasContent(node)) continue;
        if (/^H[1-6]$/.test(node.tagName)) {
          const values = [];
          while (index + 1 < nodes.length && !/^H[1-6]$/.test(nodes[index + 1].tagName)) {
            const value = nodes[++index];
            if (hasContent(value)) values.push(value);
          }
          if (values.length) {
            const item = document.createElement('div');
            item.className = 'epic-specifications-custom-item';
            const label = document.createElement('strong');
            ctx.safe.setText(label, readableLabel(node.textContent));
            item.append(label, ...values);
            result.push(item);
          } else {
            const heading = document.createElement('div');
            heading.className = 'epic-specifications-custom-subheading';
            ctx.safe.setText(heading, readableLabel(node.textContent));
            result.push(heading);
          }
        } else {
          const item = paragraphItem(node);
          if (item) {
            result.push(item);
            continue;
          }
          // Preserve rich text that does not contain explicit label/value pairs.
          const content = document.createElement('div');
          content.className = 'epic-specifications-custom-group';
          content.appendChild(node);
          result.push(content);
        }
      }
      return result.length ? result : [source];
    }
    function applyCustomGroupContent() {
      if (!groups || !customGroups.size) return;
      root.querySelectorAll('[data-section-key="specifications"] .frengee-epic-accordion').forEach(accordion => {
        const title = accordion.querySelector('.frengee-epic-accordion-head p')?.textContent.trim();
        const category = [...customGroups.keys()].find(key => groups.titles[key] === title);
        if (!category) return;
        const content = accordion.querySelector('.frengee-epic-accordion-body-content');
        if (!content) return;
        content.replaceChildren(...customSpecNodes(customGroups.get(category)));
        accordion.dataset.specificationSource = 'metafield';
      });
    }
    // The legacy Geometry tab keeps every size visible on mobile and scrolls
    // horizontally. Preserve that comparison instead of reducing it to one size.
    const createGeometryTable = ctx.utils_dom.createGeometryTable;
    ctx.utils_dom.createGeometryTable = (measurements, displaySizes) => {
      const sizes = [...new Set(measurements.flatMap(measurement =>
        (measurement.Children || []).map(child => child.FRAME_SIZE_DESC),
      ).filter(Boolean))].sort();
      return createGeometryTable(measurements, sizes.length ? sizes : displaySizes, false);
    };
    // Components Geo also compares every size in the legacy block. Keep its
    // table consistent with Geometry on mobile instead of selecting one size.
    const createComponentsTable = ctx.utils_dom.createComponentsTable;
    ctx.utils_dom.createComponentsTable = components => {
      const table = createComponentsTable(components, null);
      if (table) {
        table.classList.remove('unified-table-mobile');
        table.classList.add('frengee-unified-table');
      }
      return table;
    };
    const createTabsView = dom.createTabsView;
    let lastRenderedVariant;
    let lastMobileExperience;
    dom.createTabsView = (sections, variantId) => {
      const mobile = window.innerWidth <= 749;
      const preserveSelection = lastRenderedVariant === String(variantId)
        && lastMobileExperience === mobile;
      const selectedKey = root.querySelector('.frengee-tab-item.frengee-active')?.dataset.sectionKey;
      // Match the old Details tab's authoritative Shopify description, even
      // when the API has specifications but no overview for this variant.
      const html = dom.getProductDescriptionHtml();
      const details = ctx.showDescription && html.trim() ? [{
        key: 'description', label: 'Details', source: 'shopify',
        renderKind: 'rich_text', content: { html }, hasContent: true,
      }] : [];
      createTabsView([
        ...details,
        ...withGroupOverrides(sections).filter(section => !details.length || section.key !== 'description'),
      ], variantId);
      applyCustomGroupContent();
      const nav = root.querySelector('.frengee-tabs-list');
      nav?.classList.add('sticky-follow-header');
      // The app binds tab handlers before creating compatibility headers.
      ctx.events.setupTabHandlers();
      // Repeated API/resize renders for the same variant must not close a
      // drawer the shopper has just opened or switch their selected tab.
      if (preserveSelection && selectedKey) {
        [...root.querySelectorAll('.frengee-tab-item')]
          .find(tab => tab.dataset.sectionKey === selectedKey)?.click();
      }
      lastRenderedVariant = String(variantId);
      lastMobileExperience = mobile;
      syncAccessibility();
    };

    function syncAccessibility() {
      root.querySelectorAll('.frengee-tab-item').forEach(tab => {
        const active = tab.classList.contains('frengee-active');
        tab.setAttribute('aria-label', tab.textContent.trim());
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active || window.innerWidth <= 749 ? 0 : -1;
      });
      root.querySelectorAll('.frengee-tab-panel').forEach(panel => {
        panel.setAttribute('aria-hidden', String(!panel.classList.contains('frengee-active')));
      });
    }
    root.addEventListener('click', event => {
      if (event.target.closest('.frengee-tab-item, [data-epic-tab-close]')) {
        syncAccessibility();
      }
    });
    root.addEventListener('keydown', event => {
      const tab = event.target.closest('.frengee-tab-item');
      if (tab && ['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        tab.click();
      } else if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const tabs = [...root.querySelectorAll('.frengee-tab-item')];
        let index = tabs.indexOf(tab);
        index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs[index].click();
        tabs[index].focus();
      } else if (event.key === 'Escape' && window.innerWidth <= 749) {
        const active = root.querySelector('.frengee-tab-item.frengee-active');
        root.querySelector('.frengee-tab-panel.frengee-active [data-epic-tab-close]')?.click();
        active?.focus();
      }
    });
    // The app builds its nav after the theme has collected sticky elements.
    // Follow the same header offset when scrolling back up on desktop.
    let lastScrollTop = window.scrollY;
    const followHeader = () => {
      const nav = root.querySelector('.frengee-tabs-list');
      const scrollTop = window.scrollY;
      if (nav && window.innerWidth > 749) {
        if (scrollTop < 100 || scrollTop > lastScrollTop) {
          nav.style.top = '0px';
        } else if (scrollTop < lastScrollTop) {
          nav.style.top = `${document.querySelector('header')?.offsetHeight || 0}px`;
        }
      }
      lastScrollTop = scrollTop;
    };
    window.addEventListener('scroll', followHeader, { passive: true });
    return dom;
  };
  PS.createDom.epicThemePresentation = true;
})();
